import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import Card from './Card';
import { Card as CardV2 } from '../v2/components/card/Card';
import { connect } from 'react-redux';
import { fetchFavourites, fetchSideNav, fetchProgAvail } from '../actions';
import translate from './TranslateHOC';
import { cmsImageResize, usePrevious } from '../helpers';
import { CardGridLoading } from '../v2/components/loading/CardGridLoading';
import LoadingError from './alerts/LoadingError';

function TVHighlights({
  strings,
  items,
  itemsIsLoading,
  itemsHasErrored,
  favourites,
  signed,
  cardBtn,
  orientation,
  checkToday,
  gridType,
  playPos,
  billingBool,
  countBool,
  isSideNavToggled,
  recommended,
  playNow,
  extraFeeds,
  checkAvail,
  fetchProgAvail,
  progAvail,
  progAvailLoading,
  progAvailError
}) {
  const currentPlayPos = playPos === true ? true : false;
  const { length: itemsLength } = Object.keys(items);
  const noPadding = gridType?.includes('no-padding');
  const highlights = gridType?.includes('highlights');
  const az = gridType?.includes('az');
  const programme = gridType?.includes('programme');
  const extraFeedsLength = extraFeeds ? Object.keys(extraFeeds).length : 0;
  const prevItems = usePrevious(items);

  useEffect(() => {
    if(checkAvail && JSON.stringify(prevItems) !== JSON.stringify(items)) {
      fetchProgAvail(items);
    }
  }, [items, prevItems, checkAvail]);

  if (itemsIsLoading || (checkAvail && progAvailLoading)) {
    return (
      <CardGridContainer noPadding={noPadding}>
        <CardGridLoading orientation={orientation} />
      </CardGridContainer>
    );
  }

  if (itemsHasErrored) {
    return (
      <CardGridContainer noPadding={noPadding}>
        <LoadingError errorMessage={strings.sorryLoading} />
      </CardGridContainer>
    );
  }

  const subtitle = (key) =>
    extraFeeds[key].hide_link
      ? extraFeeds[key].hide_link_text
      : extraFeeds[key].time_text;

  return (
    <CardGridContainer noPadding={noPadding} programme={programme}>
      <CardGridInner
        className={'card-grid__inner ' + (gridType ? gridType : '')}
        gridType={gridType}
        navClosed={!isSideNavToggled}
        navOpen={isSideNavToggled}
        highlights={highlights}
        az={az}
        type={gridType}
        noPadding={noPadding}
        recommended={recommended}
      >
        {extraFeedsLength > 0 &&
          Object.keys(extraFeeds).map((key) => (
            <CardV2
              tag={strings.onlineExclusive}
              key={key}
              index={key}
              image={cmsImageResize(extraFeeds[key].image, '320x180')}
              isLive={extraFeeds[key].isLive}
              onLater={extraFeeds[key].isLater}
              isOECatchUp={extraFeeds[key].isCatchUp}
              isOEExpired={extraFeeds[key].isExpired}
              subtitle={subtitle(key)}
              title={extraFeeds[key].title}
              url={`/Live/${extraFeeds[key].url}`}
              isSlick={false}
              altText={extraFeeds[key].title}
              strings={strings}
              state={
                extraFeeds[key].isUnavailable ? 'unavailable' : 'available'
              }
            />
          ))}
        {itemsLength > 0 &&
          Object.keys(items).map((key) => (
            <CardContainer
              gridType={gridType}
              recommended={recommended}
              key={key}
            >
              <Card
                index={key}
                orientation={orientation}
                details={items[key]}
                isFav={favourites?.length > 0 && favourites.includes(key)}
                billingBool={billingBool}
                countBool={countBool}
                playPos={currentPlayPos}
                signed={signed}
                cardBtn={cardBtn}
                checkAvail={!progAvailError && checkAvail}
                checkAvailProgs={progAvail}
                isToday={checkToday}
                type={gridType}
                playNow={playNow}
              />
            </CardContainer>
          ))}
      </CardGridInner>
    </CardGridContainer>
  );
}

const CardGridContainer = styled.div`
  padding: 0 15px;
  ${(props) =>
    props.gridType === 'centered' &&
    `
    margin: auto;
    max-width: 1595px;
    padding: 12px 15px 32px;
  `}
  ${(props) => props.noPadding && `padding: 0`};
  ${(props) =>
    props.gridType === 'card-grid-container--raised' && `z-index: 10`};
  ${(props) =>
    props.gridType === 'card-grid-container--no-line' &&
    `h2 { box-shadow: none;}`};
  ${(props) => props.programme && `padding: 12px 0 32px;`};
  @media screen and (min-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const CardContainer = styled.div``;

const CardGridInner = styled.div`
  padding: 0 15px;
  ${(props) =>
    props.gridType === 'isProgramme' &&
    `
      grid-template-columns: 1fr !important;
      ${CardContainer}:not(:first-of-type) {
        display: none;
      };
  `};
  ${(props) =>
    props.recommended &&
    `
      grid-template-columns: repeat(3, 1fr) !important;
      ${CardContainer} {
        display: block;
      };
  `};
  ${(props) =>
    props.gridType === 'isProgramme' &&
    props.recommended &&
    `
      ${CardContainer}:nth-child(-n+2) {
        display: block;
      };
  `};
  ${(props) => props.highlights && `padding: 0;`};
  ${(props) =>
    props.az &&
    `grid-template-columns: repeat(auto-fill, minmax(100%, auto)) !important;`};
  ${(props) => props.navClosed && props.az && `max-width: none !important;`};
  ${(props) => props.noPadding && `padding: 0;`};

  @supports (display: grid) {
    display: grid;
    grid-gap: 16px 20px;
    grid-template-columns: 1fr 1fr;
  }
  @media screen and (min-width: 640px) {
    ${(props) =>
      props.gridType === 'isProgramme' &&
      `
        grid-template-columns: repeat(2, 1fr) !important;
        ${CardContainer}:nth-child(-n+2) {
          display: block;
        }  
      `};
      ${(props) =>
        props.recommended &&
        `
        grid-template-columns: repeat(4, 1fr) !important;
        ${CardContainer} {
          display: block !important;
        }  
      `};
  }
  @media screen and (min-width: 678px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (min-width: 768px) {
    padding: 0 15px 0 0;
    ${(props) =>
      props.highlights && `grid-template-columns: repeat(3, 1fr) !important;`};
    ${(props) =>
      props.az &&
      `grid-template-columns: repeat(auto-fill, minmax(260px, auto)) !important;`};
    ${(props) => props.noPadding && `padding: 0;`};
    ${(props) =>
      props.gridType === 'isProgramme' &&
      `
        grid-template-columns: repeat(2, 1fr) !important;
        padding: 0;
    `};
    ${(props) =>
      props.recommended &&
      `grid-template-columns: repeat(4, 1fr) !important; padding: 0;`};
  }
  @media screen and (min-width: 1024px) {
    ${(props) => props.navClosed && `grid-template-columns: repeat(4, 1fr);`};
    ${(props) =>
      props.highlights &&
      props.navClosed &&
      `grid-template-columns: repeat(4, 1fr) !important;`};
    ${(props) =>
      props.gridType === 'isProgramme' &&
      `
        grid-template-columns: repeat(4, 1fr) !important;
        ${CardContainer} {
          display: block !important;
        }
    `};
  }
  @media screen and (min-width: 1200px) {
    ${(props) => props.navOpen && `grid-template-columns: repeat(4, 1fr);`};
    ${(props) => props.navClosed && `grid-template-columns: repeat(5, 1fr);`};
  }
  @media screen and (min-width: 1270px) {
    ${(props) =>
      props.highlights && `grid-template-columns: repeat(4, 1fr) !important;`};
    ${(props) =>
      props.highlights &&
      props.navClosed &&
      `grid-template-columns: repeat(4, 1fr) !important;`};
  }
  @media screen and (min-width: 1440px) {
    ${(props) => props.navOpen && `grid-template-columns: repeat(5, 1fr);`};
    ${(props) => props.navClosed && `grid-template-columns: repeat(6, 1fr);`};
    ${(props) =>
      props.highlights &&
      props.navClosed &&
      `grid-template-columns: repeat(5, 1fr) !important;`};
    ${(props) =>
      props.gridType === 'isProgramme' &&
      `grid-template-columns: repeat(4, 1fr) !important;`};
  }
  @media screen and (min-width: 1830px) {
    ${(props) => props.navOpen && `grid-template-columns: repeat(6, 1fr);`};
    ${(props) => props.navClosed && `grid-template-columns: repeat(7, 1fr);`};
    ${(props) =>
      props.highlights && `grid-template-columns: repeat(5, 1fr) !important;`};
  @media screen and (min-width: 2078px) {
    ${(props) => props.navOpen && `grid-template-columns: repeat(7, 1fr);`};
    ${(props) => props.navClosed && `grid-template-columns: repeat(8, 1fr);`};
  }
  @media screen and (min-width: 2326px) {
    ${(props) => props.navOpen && `max-width: 2002px;`};
    ${(props) => props.navClosed && `max-width: 2230px;`};
  }
`;

function mapStateToProps(state) {
  return {
    favourites: state.favourites,
    isSideNavToggled: state.sideNav,
    progAvail: state.fetchProgAvail.success,
    progAvailLoading: state.fetchProgAvail.loading,
    progAvailError: state.fetchProgAvail.error
  };
}

export default translate(
  connect(mapStateToProps, { fetchFavourites, fetchSideNav, fetchProgAvail })(TVHighlights)
);
